import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useEvents } from '../hooks/useEvents';
import EventCard from '../components/admin/EventCard';
import EventForm from '../components/admin/EventForm';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import logger from '../utils/logger';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

const AdminDashboardPage = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { events, loading, error } = useEvents();
  const [showEventForm, setShowEventForm] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (err) {
      logger.error('Logout failed:', err);
    }
  };

  const handleCreateEvent = () => {
    setShowEventForm(true);
  };

  const handleEventFormClose = () => {
    setShowEventForm(false);
  };

  return (
    <div
      className="min-h-screen p-6 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.6), rgba(0,0,0,0.70)), url(${stoneGoldBg})`
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">MomentBox Admin</h1>
            <p className="text-gray-300 drop-shadow-lg">Welcome, {currentUser?.email}</p>
          </div>
          <div className="flex w-full md:w-auto flex-col sm:flex-row gap-3">
            <button
              onClick={handleCreateEvent}
              className="btn-primary w-full sm:w-auto"
            >
              <Plus className="w-5 h-5" />
              Create Event
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary w-full sm:w-auto"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner message="Loading events..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <ErrorMessage
            message={error}
            onRetry={() => window.location.reload()}
          />
        )}

        {/* Events Grid */}
        {!loading && !error && (
          <>
            {events.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Plus className="w-10 h-10 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3 drop-shadow-lg">
                    No Events Yet
                  </h2>
                  <p className="text-gray-300 mb-6 drop-shadow-md">
                    Create your first event to start collecting video messages from your guests.
                  </p>
                  <button
                    onClick={handleCreateEvent}
                    className="btn-primary"
                  >
                    Create Your First Event
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">
                    Your Events
                    <span className="ml-3 text-lg font-normal text-gray-400">
                      ({events.length})
                    </span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map(event => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <EventForm
          onClose={handleEventFormClose}
          onSuccess={(eventId) => {
            logger.log('Event created:', eventId);
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboardPage;
